"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getCoreHTTPHeaders = exports.CORE_HTTP_HEADERS = void 0;
const uuid_1 = require("uuid");
const lodash_1 = __importDefault(require("lodash"));
const app_config_1 = __importDefault(require("@nerdwallet/app-config"));
const cookies_1 = require("./cookies");
exports.CORE_HTTP_HEADERS = {
    COOKIE: 'Cookie',
    REFERRER: 'Referrer',
    USER_AGENT: 'User-Agent',
    X_BROWSER_SESSION_ID: 'X-Browser-Session-ID',
    X_CALLER_CLIENT_ID: 'X-Caller-Client-ID',
    X_CLIENT_VERSION: 'X-Client-Version',
    X_COOKIE_ID: 'X-Cookie-ID',
    X_HEADER_CSRF_TOKEN: 'X-Header-CSRF-Token',
    X_KOUNT_SESSION_ID: 'X-Kount-Session-ID',
    X_MEMBER_IDENTITY_ASSERTION_TOKEN: 'X-Member-Identity-Assertion-Token',
    X_ORIGINATING_APPLICATION_ID: 'X-Originating-Application-ID',
    X_ORIGINATING_IP: 'X-Originating-IP',
    X_ORIGINATING_REFERRER: 'X-Originating-Referrer',
    X_ORIGINATING_URL: 'X-Originating-URL',
    X_ORIGINATING_USER_AGENT: 'X-Originating-User-Agent',
    X_PAGEVIEW_ID: 'X-Pageview-ID',
    X_REFERRING_CALLER_CLIENT_ID: 'X-Referring-Caller-Client-ID',
    X_REQUEST_ID: 'X-Request-ID',
    X_TEST_STATE: 'X-Test-State',
    X_VISITOR_TOKEN: 'X-Visitor-Token',
    X_VISITOR_IDENTITY_ASSERTION_TOKEN: 'X-Visitor-Identity-Assertion-Token',
};
const CORE_HTTP_COOKIES = {
    ACCESS_TOKEN: 'access_token',
    JWT_ACCESS_TOKEN: 'jwt_access_token',
    CSRF_TOKEN: 'csrf_token',
    TEST_STATE: 'test_state',
    VISITOR_TOKEN: 'visitor_token',
    NW_SESSION_TOKEN: 'nw_session_token',
};
/**
 * Prefix for WP cookies used for:
 *   1. authed WP API requests
 *   2. bypassing our edge cache
 *
 * @link https://github.com/NerdWallet/edge-configs/blob/2c5684bc4fea9e052d62202dbae2cce7566d5b35/src/templates/location.conf.jinja#L133)
 */
const WP_COOKIE_PREFIX = 'wordpress_';
/**
 * Replaces unsafe control characters in a string.
 */
const replaceControlCharacters = (str, replace) => {
    if (str) {
        // eslint-disable-next-line no-control-regex
        return str.replace(/[\x00-\x1F\x7F]/g, replace);
    }
    return '';
};
/**
 * Return the full URL of the request.
 */
const getRequestUrl = (serverRequest) => `${serverRequest.protocol}://${serverRequest.get('Host')}${serverRequest.originalUrl}`;
/**
 * Calls the appropriate function to sanitize a header.
 */
const sanitizeHeader = (header) => replaceControlCharacters(header, ' ');
// Safe access to window global properties
function getWindowProp(property) {
    if (typeof window !== 'undefined') {
        return window[property];
    }
    return null;
}
/**
 * Safe access to `window.document.referrer`.
 */
function getDocumentReferrer() {
    if (getWindowProp('document')) {
        return window.document.referrer;
    }
    return null;
}
/**
 * Calls the appropriate functions to sanitize a user defined cookie.
 */
function sanitizeCookie(key, value) {
    // Key sanitization is just precautionary, there should never be a case where a user
    // is given the ability to send cookies with arbitrary keys to services
    const cookieKey = replaceControlCharacters(key, ' ').replace(/;/g, '%3B');
    const cookieValue = replaceControlCharacters(value, ' ').replace(/;/g, '%3B');
    return `${cookieKey}=${cookieValue}`;
}
/**
 * Given a cookie object returns a string for the Cookie header.
 */
function buildCookieString(cookies) {
    return Object.entries(cookies)
        .map(([name, value]) => sanitizeCookie(name, value !== null && value !== void 0 ? value : ''))
        .join('; ');
}
/**
 * Return the `Cookie` header.
 */
const getCookieHeader = ({ coreCookies, inputCookies, }) => {
    const allCookies = lodash_1.default.omit(Object.assign(Object.assign({}, inputCookies), coreCookies), 
    // The `test_state` cookie does not need to be propogated because it will be sent in the request header `X-Test-State`
    [CORE_HTTP_COOKIES.TEST_STATE]);
    // Set the cookie header, except for in browsers and mobile (when process.browser
    // is present). Browsers and mobile don't allow setting the cookie header, they
    // automatically manage cookies.
    if (typeof window === 'undefined' && !lodash_1.default.isEmpty(allCookies)) {
        return buildCookieString(allCookies);
    }
    return null;
};
/**
 * Return the `X-Browser-Session-ID` header.
 */
const getBrowserSessionID = (context) => {
    // Get the unsigned browser session id for the request header in the case that the client
    // doesn't have a valid browser_session_id in their cookies. This is used for mobile where
    // theres no initial request to www. for nginx to return a Set-Cookie header with a signed
    // browser session id.
    if (context.config && context.config.getBrowserSessionIdOverride) {
        return context.config.getBrowserSessionIdOverride(context);
    }
    if (context.serverRequest) {
        return context.serverRequest.get(exports.CORE_HTTP_HEADERS.X_BROWSER_SESSION_ID);
    }
    return null;
};
/**
 * Return the `X-Cookie-ID` header.
 */
const getCookieID = (context) => {
    // Get the unsigned cookie id for the request header in the case that the client doesn't have
    // a valid cookie_id in their cookies. This is used for mobile where theres no initial
    // request to www. for nginx to return a Set-Cookie header with a signed cookie id.
    if (context.config && context.config.getCookieIdOverride) {
        return context.config.getCookieIdOverride(context);
    }
    if (context.serverRequest) {
        return context.serverRequest.get(exports.CORE_HTTP_HEADERS.X_COOKIE_ID);
    }
    // In a browser, we rely on the `cookie_id` cookie to be sent along with the request.
    // The edge is responsible for setting the `X-Cookie-ID` header on inbound requests.
    return null;
};
/**
 * Return the CSRF Token.
 * Check the cookie value before the header so that attackers can not hijack another valid CSRF Token.
 * https://nerdwallet.atlassian.net/wiki/spaces/ENG/pages/589072198/Quick+Intro+to+CSRF+Tokens+at+NerdWallet
 */
const getCsrfToken = ({ serverRequest, coreCookies, }) => {
    var _a;
    return (_a = coreCookies[CORE_HTTP_COOKIES.CSRF_TOKEN]) !== null && _a !== void 0 ? _a : serverRequest === null || serverRequest === void 0 ? void 0 : serverRequest.get(exports.CORE_HTTP_HEADERS.X_HEADER_CSRF_TOKEN);
};
/**
 * Return the `X-Originating-IP` header.
 */
const getOriginatingIP = ({ serverRequest, }) => {
    if (serverRequest) {
        return serverRequest.ip;
    }
    return null;
};
/**
 * Return the `X-Originating-Referrer` header.
 */
const getOriginatingReferrer = ({ config, serverRequest, }) => {
    if (serverRequest) {
        // Forward `X-Originating-Referrer` if it exists, otherwise look at the request's referrer.
        return (serverRequest.get(exports.CORE_HTTP_HEADERS.X_ORIGINATING_REFERRER) ||
            serverRequest.get(exports.CORE_HTTP_HEADERS.REFERRER));
    }
    if (config && config.nwa) {
        return config.nwa.getGlobalProp('referrer') || getDocumentReferrer();
    }
    return null;
};
/**
 * Return the `X-Request-ID` header.
 */
const getRequestID = ({ serverRequest, }) => {
    if (serverRequest) {
        const requestId = serverRequest.get(exports.CORE_HTTP_HEADERS.X_REQUEST_ID);
        if (requestId) {
            return requestId;
        }
    }
    // Generate a new UUID if one was not present.
    return (0, uuid_1.v4)();
};
/**
 * Return the `X-Originating-URL` header.
 */
const getOriginatingURL = ({ config, serverRequest, }) => {
    if (config.getUrl) {
        return config.getUrl();
    }
    if (serverRequest) {
        // Forward `X-Originating-URL` if it exists, otherwise look at the request url.
        return (serverRequest.get(exports.CORE_HTTP_HEADERS.X_ORIGINATING_URL) ||
            getRequestUrl(serverRequest));
    }
    if (config && config.nwa) {
        return config.nwa.getGlobalProp('url');
    }
    if (getWindowProp('location')) {
        return window.location.href;
    }
    return null;
};
/**
 * Return the `X-Originating-User-Agent` header.
 */
const getOriginatingUserAgent = ({ serverRequest, }) => {
    // Forward `X-Originating-User-Agent` if it exists, otherwise look at the request's referrer.
    if (serverRequest) {
        return (serverRequest.get(exports.CORE_HTTP_HEADERS.X_ORIGINATING_USER_AGENT) ||
            serverRequest.get(exports.CORE_HTTP_HEADERS.USER_AGENT));
    }
    if (getWindowProp('navigator')) {
        return window.navigator.userAgent;
    }
    return null;
};
/**
 * Return the `X-Pageview-ID` header.
 */
const getPageviewID = ({ config }) => {
    if (config.getPageviewId) {
        return config.getPageviewId();
    }
    if (config && config.nwa) {
        return config.nwa.getGlobalProp('pageViewId');
    }
    return null;
};
/**
 * Return the `X-Test-State` header.
 *
 * Propagate the test state header if this is a server request.
 * Part of the "QA Test States" protocol - see https://nerdwallet.atlassian.net/wiki/spaces/ENG/pages/515539477/2018-12-20+QA+Test+States
 * TODO(UDC-4431) - Replace project spec with cleaner docs once complete.
 */
const getTestState = ({ serverRequest, coreCookies, }) => {
    if (serverRequest && serverRequest.get(exports.CORE_HTTP_HEADERS.X_TEST_STATE)) {
        return serverRequest.get(exports.CORE_HTTP_HEADERS.X_TEST_STATE);
    }
    return coreCookies[CORE_HTTP_COOKIES.TEST_STATE];
};
/**
 * Return the `X-Vistor-Token` header.
 */
const getVisitorToken = ({ serverRequest, }) => {
    if (serverRequest) {
        return serverRequest.get('X-Visitor-Token');
    }
    return null;
};
/**
 * Return the `X-Member-Identity-Assertion-Token` header.
 */
const getMemberIdentityAssertionToken = ({ serverRequest, }) => {
    if (serverRequest) {
        return serverRequest.get(exports.CORE_HTTP_HEADERS.X_MEMBER_IDENTITY_ASSERTION_TOKEN);
    }
    // On clients, the member_identity_assertion_token cookie will
    // automatically be sent, so no header is needed.
    return null;
};
/**
 * Return the `X-Visitor-Identity-Assertion-Token` header.
 */
const getVisitorIdentityAssertionToken = ({ serverRequest, }) => {
    if (serverRequest) {
        return serverRequest.get(exports.CORE_HTTP_HEADERS.X_VISITOR_IDENTITY_ASSERTION_TOKEN);
    }
    // On clients, the visitor_identity_assertion_token cookie will
    // automatically be sent, so no header is needed.
    return null;
};
/**
 * Return WP cookies via the `getCookies` helper.
 */
const pickWpCookies = (existingCookies) => lodash_1.default.pickBy(existingCookies, (value, key) => key.indexOf(WP_COOKIE_PREFIX) === 0);
/**
 * Returns NerdWallet snapshot cookies that start with "upstream-override-"
 */
const pickSnapshotCookies = (existingCookies) => lodash_1.default.pickBy(existingCookies, (value, key) => key.indexOf('upstream-override-') === 0);
/**
 * Return the core cookies via the `getCookies` helper.
 */
const getCoreCookies = (context) => (0, cookies_1.getCookies)(context).then((existingCookies) => {
    const coreCookies = lodash_1.default.pickBy({
        [CORE_HTTP_COOKIES.ACCESS_TOKEN]: existingCookies[CORE_HTTP_COOKIES.ACCESS_TOKEN],
        [CORE_HTTP_COOKIES.JWT_ACCESS_TOKEN]: existingCookies[CORE_HTTP_COOKIES.JWT_ACCESS_TOKEN],
        [CORE_HTTP_COOKIES.CSRF_TOKEN]: existingCookies[CORE_HTTP_COOKIES.CSRF_TOKEN],
        [CORE_HTTP_COOKIES.TEST_STATE]: existingCookies[CORE_HTTP_COOKIES.TEST_STATE],
        [CORE_HTTP_COOKIES.VISITOR_TOKEN]: existingCookies[CORE_HTTP_COOKIES.VISITOR_TOKEN],
        [CORE_HTTP_COOKIES.VISITOR_TOKEN]: existingCookies[CORE_HTTP_COOKIES.VISITOR_TOKEN],
        [CORE_HTTP_COOKIES.NW_SESSION_TOKEN]: existingCookies[CORE_HTTP_COOKIES.NW_SESSION_TOKEN],
    });
    const wpCookies = pickWpCookies(existingCookies);
    const snapshotCookies = pickSnapshotCookies(existingCookies);
    return Object.assign(Object.assign(Object.assign({}, coreCookies), wpCookies), snapshotCookies);
});
/**
 * Returns an object with all of the core http headers set.
 */
const getCoreHTTPHeaders = ({ url, config, serverRequest, cookies: inputCookies, referringClientID, 
// default to `window.__KOUNT_SESSION_ID__` set by Global Markup
kountSessionID = getWindowProp('__KOUNT_SESSION_ID__'), }) => getCoreCookies({ url, config, serverRequest }).then((coreCookies) => {
    var _a;
    const headers = {
        [exports.CORE_HTTP_HEADERS.COOKIE]: getCookieHeader({
            coreCookies,
            inputCookies,
        }),
        [exports.CORE_HTTP_HEADERS.X_BROWSER_SESSION_ID]: getBrowserSessionID({
            url,
            config,
            serverRequest,
        }),
        [exports.CORE_HTTP_HEADERS.X_CALLER_CLIENT_ID]: config.clientID,
        [exports.CORE_HTTP_HEADERS.X_CLIENT_VERSION]: lodash_1.default.get(app_config_1.default, '_meta.version'),
        [exports.CORE_HTTP_HEADERS.X_COOKIE_ID]: getCookieID({ config, serverRequest }),
        [exports.CORE_HTTP_HEADERS.X_HEADER_CSRF_TOKEN]: getCsrfToken({
            serverRequest,
            coreCookies,
        }),
        [exports.CORE_HTTP_HEADERS.X_KOUNT_SESSION_ID]: kountSessionID,
        [exports.CORE_HTTP_HEADERS.X_MEMBER_IDENTITY_ASSERTION_TOKEN]: getMemberIdentityAssertionToken({ serverRequest }),
        [exports.CORE_HTTP_HEADERS.X_ORIGINATING_APPLICATION_ID]: config.applicationID,
        [exports.CORE_HTTP_HEADERS.X_ORIGINATING_IP]: getOriginatingIP({ serverRequest }),
        [exports.CORE_HTTP_HEADERS.X_ORIGINATING_REFERRER]: getOriginatingReferrer({
            config,
            serverRequest,
        }),
        [exports.CORE_HTTP_HEADERS.X_ORIGINATING_URL]: getOriginatingURL({
            serverRequest,
            config,
        }),
        [exports.CORE_HTTP_HEADERS.X_ORIGINATING_USER_AGENT]: getOriginatingUserAgent({
            serverRequest,
        }),
        [exports.CORE_HTTP_HEADERS.X_PAGEVIEW_ID]: getPageviewID({ config }),
        [exports.CORE_HTTP_HEADERS.X_REFERRING_CALLER_CLIENT_ID]: referringClientID,
        [exports.CORE_HTTP_HEADERS.X_REQUEST_ID]: getRequestID({
            serverRequest,
        }),
        [exports.CORE_HTTP_HEADERS.X_TEST_STATE]: getTestState({
            serverRequest,
            coreCookies,
        }),
        [exports.CORE_HTTP_HEADERS.X_VISITOR_TOKEN]: getVisitorToken({ serverRequest }),
        [exports.CORE_HTTP_HEADERS.X_VISITOR_IDENTITY_ASSERTION_TOKEN]: getVisitorIdentityAssertionToken({ serverRequest }),
    };
    const abAndffHeaders = lodash_1.default.pickBy((_a = serverRequest === null || serverRequest === void 0 ? void 0 : serverRequest.headers) !== null && _a !== void 0 ? _a : {}, (val, h) => h.startsWith('test_') || h.startsWith('feat_'));
    const allHeaders = Object.assign(Object.assign({}, headers), abAndffHeaders);
    // Sanitize all headers
    return Object.keys(allHeaders).reduce((accumulator, headerName) => {
        // Remove any values that are `null` or `undefined`.
        // Node and browsers normally ignore null header values, but React Native's Android HTTP
        // implementation will crash if a null header value is present.
        if (!lodash_1.default.isNil(allHeaders[headerName])) {
            accumulator[headerName] = sanitizeHeader(allHeaders[headerName]);
        }
        return accumulator;
    }, {});
});
exports.getCoreHTTPHeaders = getCoreHTTPHeaders;
